import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiGet } from '@shared/api/httpClient';
import type {
  ActivityLogRecord,
  ActivityResponse,
  ClientRecord,
  ClientsResponse,
  HealthResponse,
  JobsResponse,
  MeResponse,
  ReportJobRecord,
} from '@shared/types/api';
import { friendlyLabel, normalizeLabelKey } from '@shared/utils/friendlyLabels';
import { displayNameForIdentity } from '@shared/utils/privacy';

type DashboardData = {
  health?: HealthResponse;
  me?: MeResponse;
  clients: ClientRecord[];
  jobs: ReportJobRecord[];
  activity: ActivityLogRecord[];
};

type LoadState = {
  loading: boolean;
  data?: DashboardData;
  error?: string;
};

type ReadinessCounts = {
  pass: number;
  partial: number;
  fail: number;
  notApplicable: number;
  total: number;
  supported: number;
  readinessPercent: number;
};

type ReadinessSourceRecord = Record<string, unknown> & {
  clientId?: string;
  clientName?: string;
  companyName?: string;
  packageName?: string;
  packageId?: string;
  jobId?: string;
  status?: string;
  displayStatus?: string;
  lifecycleStatus?: string;
  packageLifecycleStatus?: string;
  jobStatus?: string;
  finalPackageDeliveryStatus?: string;
  updatedAt?: string;
  createdAt?: string;
  completedAt?: string;
};

function value(input: unknown): string {
  if (input === undefined || input === null || input === '') return '—';
  if (Array.isArray(input)) return input.join(', ') || '—';
  if (typeof input === 'object') return JSON.stringify(input);
  return String(input);
}

function numberValue(input: unknown): number {
  if (typeof input === 'number' && Number.isFinite(input)) return input;
  if (typeof input === 'string' && input.trim()) {
    const parsed = Number(input);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}


function recordNumber(record: Record<string, unknown> | undefined, ...keys: string[]): number {
  const item = record ?? {};
  for (const key of keys) {
    const value = numberValue(item[key]);
    if (value) return value;
  }
  return 0;
}

function textValue(record: Record<string, unknown> | undefined, ...keys: string[]): string {
  const item = record ?? {};
  for (const key of keys) {
    const value = item[key];
    if (value === undefined || value === null) continue;
    const cleaned = String(value).trim();
    if (cleaned) return cleaned;
  }
  return '';
}

function scopeSummaryRecord(record: Record<string, unknown> | undefined): Record<string, unknown> {
  const nested = (record ?? {}).scopeSummary;
  return nested && typeof nested === 'object' && !Array.isArray(nested) ? nested as Record<string, unknown> : {};
}

function scopeClassificationCounts(record: Record<string, unknown> | undefined) {
  const nested = scopeSummaryRecord(record);
  const unknownScopeAssets = recordNumber(record, 'scopeUnknownScopeAssets') || recordNumber(nested, 'unknownScopeAssets');
  const likelyAwsManaged = recordNumber(record, 'scopeUnknownScopeLikelyAwsManaged') || recordNumber(nested, 'unknownScopeLikelyAwsManaged');
  const likelyControlTower = recordNumber(record, 'scopeUnknownScopeLikelyControlTower') || recordNumber(nested, 'unknownScopeLikelyControlTower');
  const likelyCdkBootstrap = recordNumber(record, 'scopeUnknownScopeLikelyCdkBootstrap') || recordNumber(nested, 'unknownScopeLikelyCdkBootstrap');
  const likelyPlatformSupport = recordNumber(record, 'scopeUnknownScopeLikelyPlatformSupport') || recordNumber(nested, 'unknownScopeLikelyPlatformSupport');
  const clientCreatedUnknown = recordNumber(record, 'scopeUnknownScopeClientCreatedUnknown') || recordNumber(nested, 'unknownScopeClientCreatedUnknown');
  const requiresClientReview = recordNumber(record, 'scopeUnknownScopeRequiresClientReview') || recordNumber(nested, 'unknownScopeRequiresClientReview');
  const likelyAwsOrPlatformGenerated = likelyAwsManaged + likelyControlTower + likelyCdkBootstrap + likelyPlatformSupport;
  const classificationPresent = textValue(record, 'scopeUnknownScopeClassificationPresent').toLowerCase() === 'true'
    || textValue(nested, 'unknownScopeClassificationPresent').toLowerCase() === 'true'
    || likelyAwsOrPlatformGenerated > 0
    || clientCreatedUnknown > 0
    || requiresClientReview > 0;

  return { classificationPresent, unknownScopeAssets, likelyAwsManaged, likelyControlTower, likelyCdkBootstrap, likelyPlatformSupport, likelyAwsOrPlatformGenerated, clientCreatedUnknown, requiresClientReview };
}

function formatDate(input: unknown): string {
  const raw = typeof input === 'string' ? input : '';
  if (!raw) return '—';
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return raw;
  return parsed.toLocaleString();
}

function rawStatusFor(job: ReportJobRecord): string {
  return value(job.displayStatus || job.jobStatus || job.packageLifecycleStatus || job.lifecycleStatus || job.status);
}

function statusFor(job: ReportJobRecord): string {
  return friendlyLabel(rawStatusFor(job));
}

function rawActivityStatus(activity: ActivityLogRecord): string {
  return value(activity.displayStatus || activity.status || activity.jobStatus || activity.packageLifecycleStatus || activity.clientUploadStatus);
}

function activityStatus(activity: ActivityLogRecord): string {
  return friendlyLabel(rawActivityStatus(activity));
}

function activityTime(activity: ActivityLogRecord): string {
  return value(activity.eventTime || activity.updatedAt || activity.createdAt);
}

function activityDetail(activity: ActivityLogRecord): Record<string, unknown> {
  const detail = (activity as Record<string, unknown>).detail;
  if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return {};
  return detail as Record<string, unknown>;
}

function activityField(activity: ActivityLogRecord, ...fieldNames: string[]): string {
  const record = activity as Record<string, unknown>;
  const detail = activityDetail(activity);

  for (const fieldName of fieldNames) {
    const directValue = record[fieldName];
    if (directValue !== undefined && directValue !== null && directValue !== '') return String(directValue);

    const detailValue = detail[fieldName];
    if (detailValue !== undefined && detailValue !== null && detailValue !== '') return String(detailValue);
  }

  return '';
}

function activityPackageName(activity: ActivityLogRecord): string {
  return activityField(activity, 'packageName', 'evidencePackageFileName', 'finalAuditorPackageName', 'packageId');
}

function activityDisplayMessage(activity: ActivityLogRecord): string {
  const explicitMessage = activityField(activity, 'message');
  if (explicitMessage) return explicitMessage;

  const status = normalizeLabelKey(rawActivityStatus(activity));
  const packageName = activityPackageName(activity);
  const packageSuffix = packageName ? ` for ${packageName}` : '';

  const statusMessages: Record<string, string> = {
    ADMIN_ORCHESTRATION_REQUEST: 'Admin queued an evidence workflow for the client.',
    ORCHESTRATION_STARTED: 'Centralized CMMC orchestration workflow started.',
    CLIENT_APPROVAL_REQUESTED: 'Client approval requested before evidence generation.',
    CLIENT_APPROVAL_EMAIL_SENT: 'Client approval email processed.',
    CLIENT_APPROVED_EVIDENCE_GENERATION: 'Client approved evidence generation.',
    CLIENT_DECLINED_EVIDENCE_GENERATION: 'Client declined the workflow request.',
    EVIDENCE_VALIDATION_STARTED: 'Evidence validation started.',
    EVIDENCE_VALIDATED: 'Evidence package validated.',
    CONTROL_MATRIX_STARTED: 'Control coverage matrix generation started.',
    CONTROL_MATRIX_BUILT: 'Control coverage matrix built.',
    REPORT_GENERATION_SKIPPED: 'Report generation was skipped for this workflow.',
    EVIDENCE_ARCHIVE_REBUILT: 'Evidence archive rebuilt.',
    EVIDENCE_PACKAGE_UPLOADED: `Evidence package uploaded${packageSuffix}.`,
    AUDITOR_PACKAGE_READY: `Final auditor package ready${packageSuffix}.`,
    FINAL_PACKAGE_DELIVERED: `Final auditor package delivered${packageSuffix}.`,
    UPLOAD_ACCESS_KEY_CREATED: 'Client upload access key created.',
    PACKAGE_DOWNLOAD_URL_GENERATED: 'Package download URL generated.',
    CLIENT_APPROVAL_VIEWED: 'Client viewed an evidence generation approval request.',
  };

  return statusMessages[status] || value(activity.eventType || activity.jobId || status);
}

const LOW_VALUE_DASHBOARD_ACTIVITY_STATUSES = new Set([
  'CLIENT_APPROVAL_VIEWED',
  'PACKAGE_DOWNLOAD_URL_GENERATED',
]);

function isDashboardActivity(activity: ActivityLogRecord): boolean {
  return !LOW_VALUE_DASHBOARD_ACTIVITY_STATUSES.has(normalizeLabelKey(rawActivityStatus(activity)));
}

function updatedAt(record: { updatedAt?: string; createdAt?: string; completedAt?: string }): string {
  return String(record.updatedAt || record.completedAt || record.createdAt || '');
}

function isActiveClient(client: ClientRecord): boolean {
  return String(client.lifecycle || client.lifecycleStatus || client.status || '').toLowerCase() === 'active';
}

function isProblemStatus(status: unknown): boolean {
  const text = normalizeLabelKey(status);
  return text.includes('FAIL') || text.includes('ERROR') || text.includes('DENIED');
}

function isInProgressStatus(status: unknown): boolean {
  const text = normalizeLabelKey(status);
  return text.includes('RUNNING') || text.includes('PROCESSING') || text.includes('PENDING') || text.includes('QUEUED') || text.includes('REQUESTED');
}

function latestReadinessJob(jobs: ReportJobRecord[]): ReportJobRecord | undefined {
  return [...jobs]
    .filter((job) => readinessCounts(job).total > 0)
    .sort((a, b) => updatedAt(b).localeCompare(updatedAt(a)))[0];
}

function readinessCounts(record?: ReadinessSourceRecord): ReadinessCounts {
  const pass = numberValue(record?.passCount ?? record?.finalReadinessPassControls);
  const partial = numberValue(record?.partialCount ?? record?.finalReadinessPartialControls);
  const fail = numberValue(record?.failCount ?? record?.finalReadinessFailControls);
  const notApplicable = numberValue(record?.notApplicableCount ?? record?.finalReadinessNotApplicableControls);
  const total = numberValue(record?.totalControls ?? record?.finalReadinessTotalControls) || pass + partial + fail + notApplicable;
  const supported = pass + notApplicable;
  const explicitPercent = numberValue(record?.readinessPercent);
  const readinessPercent = explicitPercent > 0 ? explicitPercent : total > 0 ? Math.round((supported / total) * 100) : 0;

  return { pass, partial, fail, notApplicable, total, supported, readinessPercent };
}

function readinessUpdatedAt(record: ReadinessSourceRecord): string {
  return textValue(
    record,
    'latestPackageUpdatedAt',
    'latestPackageGeneratedUtc',
    'generatedUtc',
    'completedAt',
    'updatedAt',
    'createdAt',
  );
}

function isFinalReadinessSource(record: ReadinessSourceRecord): boolean {
  return normalizeLabelKey(
    textValue(record, 'latestPackageType', 'packageType')
      || textValue(record, 'latestPackageStage', 'packageStage')
      || textValue(record, 'latestPackageStatus', 'packageLifecycleStatus', 'finalPackageDeliveryStatus', 'status'),
  ).includes('FINAL');
}

function latestReadinessSource(clients: ClientRecord[], jobs: ReportJobRecord[]): ReadinessSourceRecord | undefined {
  const clientSources = clients
    .filter((client) => readinessCounts(client).total > 0)
    .sort((a, b) => {
      const finalDelta = Number(isFinalReadinessSource(b)) - Number(isFinalReadinessSource(a));
      if (finalDelta) return finalDelta;
      return readinessUpdatedAt(b).localeCompare(readinessUpdatedAt(a));
    });

  if (clientSources.length) return clientSources[0];

  return latestReadinessJob(jobs);
}

function rawReadinessStatus(record: ReadinessSourceRecord): string {
  return value(
    textValue(
      record,
      'latestPackageStatus',
      'finalPackageDeliveryStatus',
      'packageLifecycleStatus',
      'jobStatus',
      'lifecycleStatus',
      'displayStatus',
      'status',
    ),
  );
}

function readinessSourceTitle(record: ReadinessSourceRecord): string {
  return value(
    textValue(
      record,
      'latestPackageName',
      'packageName',
      'latestPackageId',
      'packageId',
      'jobId',
      'companyName',
      'clientName',
      'clientId',
    ),
  );
}

function readinessSourceDescription(record: ReadinessSourceRecord): string {
  const clientName = textValue(record, 'companyName', 'clientName', 'clientId');
  const packageType = friendlyLabel(textValue(record, 'latestPackageType', 'packageType'));
  const generatedAt = formatDate(readinessUpdatedAt(record));
  const sourceParts = [clientName, packageType !== '—' ? packageType : '', generatedAt !== '—' ? generatedAt : ''].filter(Boolean);

  return sourceParts.length
    ? `Shows exact readiness counts indexed on the current package summary: ${sourceParts.join(' · ')}.`
    : 'Shows exact readiness counts indexed on the current package summary.';
}

function dashboardStatusClass(status: unknown): string {
  const text = normalizeLabelKey(status);
  if (text.includes('CLIENT_APPROVAL_VIEWED')) return 'dashboard-status-viewed';
  if (isProblemStatus(text)) return 'dashboard-status-bad';
  if (isInProgressStatus(text)) return 'dashboard-status-warn';
  if (text.includes('READY') || text.includes('SUCCESS') || text.includes('UPLOADED') || text.includes('INDEXED') || text.includes('ACTIVE')) {
    return 'dashboard-status-good';
  }
  return 'dashboard-status-neutral';
}

function groupList(me?: MeResponse): string[] {
  if (!me?.groups) return [];
  if (Array.isArray(me.groups)) return me.groups.map(String);
  return String(me.groups).split(',').map((item) => item.trim()).filter(Boolean);
}

function adminDisplayName(me?: MeResponse): string {
  return displayNameForIdentity(me as unknown as Record<string, unknown> | undefined, 'SURELINC admin');
}

function MetricCard({ label, value: metricValue, helper, tone = 'blue' }: { label: string; value: string | number; helper: string; tone?: 'blue' | 'green' | 'amber' | 'red' | 'slate' }) {
  return (
    <article className={`metric-card metric-${tone}`}>
      <span>{label}</span>
      <strong>{metricValue}</strong>
      <p>{helper}</p>
    </article>
  );
}

function ActionCard({ to, title, description }: { to: string; title: string; description: string }) {
  return (
    <Link className="hero-action-card" to={to}>
      <strong>{title}</strong>
      <span>{description}</span>
    </Link>
  );
}


function ResourceScopeReviewCard({ latestJob }: { latestJob?: ReportJobRecord }) {
  const scope = scopeClassificationCounts(latestJob as Record<string, unknown> | undefined);
  if (!latestJob || !scope.classificationPresent) return null;

  return (
    <article className="card">
      <div className="dashboard-section-header compact">
        <div>
          <span className="eyebrow">Scope review context</span>
          <h3>Resource Scope Review</h3>
          <p className="muted-text">Indexed UnknownScope classification context for package review. This is not a readiness score.</p>
        </div>
      </div>
      <div className="admin-user-summary">
        <div><span>Unknown-scope assets</span><strong>{scope.unknownScopeAssets}</strong></div>
        <div><span>Likely AWS-managed/security-service</span><strong>{scope.likelyAwsManaged}</strong></div>
        <div><span>Likely Control Tower baseline</span><strong>{scope.likelyControlTower}</strong></div>
        <div><span>Likely CDK bootstrap</span><strong>{scope.likelyCdkBootstrap}</strong></div>
        <div><span>Likely platform support</span><strong>{scope.likelyPlatformSupport}</strong></div>
        <div><span>Client-created unknown</span><strong>{scope.clientCreatedUnknown}</strong></div>
        <div><span>Requires client review</span><strong>{scope.requiresClientReview}</strong></div>
      </div>
    </article>
  );
}

function ReadinessGauge({ counts, sourceRecord }: { counts?: ReadinessCounts; sourceRecord?: ReadinessSourceRecord }) {
  if (!sourceRecord || !counts) {
    return (
      <article className="card dashboard-readiness-card">
        <div className="dashboard-section-header">
          <div>
            <span className="eyebrow">Current readiness snapshot</span>
            <h3>Readiness data not indexed</h3>
            <p className="muted-text">No client or job record with explicit readiness counts was returned by the API. The dashboard does not infer readiness counts or display placeholder zero values.</p>
          </div>
          <span className="status-pill dashboard-status-warn">Unavailable</span>
        </div>
      </article>
    );
  }

  const barStyle = { width: `${Math.min(100, Math.max(0, counts.readinessPercent))}%` };
  const rawStatus = rawReadinessStatus(sourceRecord);
  const currentStatus = friendlyLabel(rawStatus);

  return (
    <article className="card dashboard-readiness-card">
      <div className="dashboard-section-header">
        <div>
          <span className="eyebrow">Current readiness snapshot</span>
          <h3>{readinessSourceTitle(sourceRecord)}</h3>
          <p className="muted-text">{readinessSourceDescription(sourceRecord)}</p>
        </div>
        <span className={`status-pill ${dashboardStatusClass(rawStatus)}`}>{currentStatus}</span>
      </div>

      <div className="readiness-meter" aria-label={`Readiness ${counts.readinessPercent}%`}>
        <div className="readiness-meter-fill" style={barStyle} />
      </div>
      <div className="readiness-meter-labels">
        <strong>{counts.readinessPercent}% supported</strong>
        <span>{counts.supported} of {counts.total} controls supported or scoped out</span>
      </div>

      <div className="readiness-count-grid">
        <div className="readiness-count pass"><span>PASS</span><strong>{counts.pass}</strong></div>
        <div className="readiness-count partial"><span>PARTIAL</span><strong>{counts.partial}</strong></div>
        <div className="readiness-count fail"><span>FAIL</span><strong>{counts.fail}</strong></div>
        <div className="readiness-count na"><span>N/A</span><strong>{counts.notApplicable}</strong></div>
        <div className="readiness-count total"><span>TOTAL</span><strong>{counts.total}</strong></div>
      </div>
    </article>
  );
}

export default function DashboardPage() {
  const [state, setState] = useState<LoadState>({ loading: true });

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const [healthResponse, meResponse, clientsResponse, jobsResponse, activityResponse] = await Promise.all([
          apiGet<HealthResponse>('/health'),
          apiGet<MeResponse>('/me'),
          apiGet<ClientsResponse>('/clients', { query: { limit: 100 } }),
          apiGet<JobsResponse>('/jobs', { query: { limit: 10 } }),
          apiGet<ActivityResponse>('/activity', { query: { limit: 30 } }),
        ]);

        if (!cancelled) {
          setState({
            loading: false,
            data: {
              health: healthResponse,
              me: meResponse,
              clients: clientsResponse.clients ?? [],
              jobs: jobsResponse.jobs ?? [],
              activity: activityResponse.activity ?? [],
            },
          });
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Unable to load dashboard data.';
        if (!cancelled) {
          setState({ loading: false, error: message });
        }
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
  }, []);

  const clients = state.data?.clients ?? [];
  const jobs = state.data?.jobs ?? [];
  const activity = state.data?.activity ?? [];
  const dashboardActivity = useMemo(() => activity.filter(isDashboardActivity).slice(0, 5), [activity]);
  const activeClients = clients.filter(isActiveClient).length;
  const latestReadiness = useMemo(() => latestReadinessSource(clients, jobs), [clients, jobs]);
  const latestReadinessJobForScope = useMemo(() => latestReadinessJob(jobs), [jobs]);
  const counts = useMemo(() => (latestReadiness ? readinessCounts(latestReadiness) : undefined), [latestReadiness]);
  const failedJobs = jobs.filter((job) => isProblemStatus(statusFor(job))).length;
  const inProgressJobs = jobs.filter((job) => isInProgressStatus(statusFor(job))).length;
  const finalPackages = jobs.filter((job) => String(job.finalPackageDeliveryStatus || job.packageLifecycleStatus || '').toUpperCase().includes('READY')).length;
  const groups = groupList(state.data?.me);

  return (
    <div className="page-stack dashboard-page">
      <section className="admin-dashboard-hero">
        <div>
          <span className="eyebrow">Admin operations overview</span>
          <h2>Readiness Platform Command Center</h2>
          <p>
            Monitor client onboarding, evidence workflow health, package readiness, and recent platform activity from one demo-ready view.
          </p>
          <div className="hero-actions">
            <ActionCard to="/clients" title="Manage clients" description="Register clients, verify lifecycle status, and launch workflows." />
            <ActionCard to="/jobs" title="Review jobs" description="Track evidence generation, package readiness, and report delivery." />
            <ActionCard to="/activity" title="View activity" description="Open the operational audit trail for workflow traceability." />
          </div>
        </div>
        <div className="hero-platform-status" aria-label="Platform health">
          <span className={`platform-health-dot ${state.data?.health?.status === 'ok' ? 'online' : 'checking'}`} />
          <span>{state.loading ? 'Checking platform' : (state.data?.health?.status === 'ok' ? 'Platform online' : 'Platform health needs review')}</span>
        </div>
      </section>

      {state.error && <p className="error-text">{state.error}</p>}
      {state.loading && <p className="muted-text">Loading admin dashboard metrics...</p>}

      {!state.loading && !state.error && (
        <>
          <section className="dashboard-metric-grid" aria-label="Platform metrics">
            <MetricCard label="Clients" value={clients.length} helper={`${activeClients} active client${activeClients === 1 ? '' : 's'}`} tone="blue" />
            <MetricCard label="Recent jobs" value={jobs.length} helper={`${inProgressJobs} in progress from latest results`} tone="amber" />
            <MetricCard label="Final packages" value={finalPackages} helper="Ready or delivered package records in latest jobs" tone="green" />
            <MetricCard label="Issues" value={failedJobs} helper="Failed, error, or denied records in latest jobs" tone={failedJobs ? 'red' : 'slate'} />
          </section>

          <section className="dashboard-two-column">
            <ReadinessGauge counts={counts} sourceRecord={latestReadiness} />

            <ResourceScopeReviewCard latestJob={latestReadinessJobForScope} />

            <article className="card">
              <div className="dashboard-section-header compact">
                <div>
                  <span className="eyebrow">Administrator access</span>
                  <h3>{adminDisplayName(state.data?.me)}</h3>
                  <p className="muted-text">Shows the signed-in role context used to authorize platform administration actions without exposing login identifiers.</p>
                </div>
              </div>
              <div className="admin-user-summary">
                <div><span>Admin access</span><strong>{state.data?.me?.isAdmin ? 'Enabled' : 'Disabled'}</strong></div>
                <div><span>Authorization</span><strong>{state.data?.me?.isAdmin ? 'Platform administrator' : 'Limited access'}</strong></div>
                <div><span>Groups</span><strong>{groups.length ? groups.join(', ') : '—'}</strong></div>
              </div>
            </article>
          </section>

          <section className="dashboard-two-column">
            <article className="card">
              <div className="dashboard-section-header compact">
                <div>
                  <span className="eyebrow">Recent workflow jobs</span>
                  <h3>Latest evidence and package activity</h3>
                  <p className="muted-text">Use this during demos to show that the platform is processing real client workflow records.</p>
                </div>
                <Link className="secondary-button" to="/jobs">Open jobs</Link>
              </div>
              {jobs.length === 0 ? (
                <p className="muted-text">No recent jobs found.</p>
              ) : (
                <div className="dashboard-list">
                  {jobs.slice(0, 5).map((job, index) => (
                    <div className="dashboard-list-row" key={`${job.clientId || 'client'}-${job.jobId || index}`}>
                      <div>
                        <strong>{value(job.clientName || job.clientId)}</strong>
                        <span>{value(job.packageName || job.jobId)}</span>
                      </div>
                      <span className={`status-pill ${dashboardStatusClass(rawStatusFor(job))}`}>{statusFor(job)}</span>
                    </div>
                  ))}
                </div>
              )}
            </article>

            <article className="card">
              <div className="dashboard-section-header compact">
                <div>
                  <span className="eyebrow">Recent activity</span>
                  <h3>Operational audit trail</h3>
                  <p className="muted-text">Shows the latest platform events, useful for demonstrating traceability and monitoring.</p>
                </div>
                <Link className="secondary-button" to="/activity">Open activity</Link>
              </div>
              {activity.length === 0 ? (
                <p className="muted-text">No recent activity found.</p>
              ) : dashboardActivity.length === 0 ? (
                <p className="muted-text">Only low-value view or download events were found in the latest activity. Open the full Activity page to review the complete audit trail.</p>
              ) : (
                <div className="dashboard-list">
                  {dashboardActivity.map((item, index) => {
                    const rawStatus = rawActivityStatus(item);
                    const status = activityStatus(item);
                    const packageName = activityPackageName(item);
                    return (
                      <div
                        className={`dashboard-list-row dashboard-activity-row ${normalizeLabelKey(rawStatus) === 'CLIENT_APPROVAL_VIEWED' ? 'dashboard-activity-viewed' : ''}`}
                        key={`${item.clientId || 'client'}-${item.eventId || item.activityId || item.eventTimeEventId || index}`}
                      >
                        <div className="dashboard-activity-main">
                          <strong>{value(item.clientName || item.clientId)}</strong>
                          <span className="dashboard-activity-message">{activityDisplayMessage(item)}</span>
                          <span className="dashboard-activity-time">{formatDate(activityTime(item))}</span>
                          {packageName && <span className="dashboard-activity-package">Package: {packageName}</span>}
                        </div>
                        <span className={`status-pill ${dashboardStatusClass(rawStatus)}`}>{status}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </article>
          </section>
        </>
      )}
    </div>
  );
}
